import { useNavigate } from "react-router-dom";
import { useTheme } from "@/components/ThemeProvider";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigation } from "@/contexts/NavigationContext";
import { Menu, Sun, Moon, Monitor, Bell, Search, LogOut, User, Settings, ListTodo } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Logo } from "@/components/Logo";
import { WorkspaceNav } from "@/components/layout/WorkspaceNav";
import { QuickCreate } from "@/components/layout/QuickCreate";
import { RunningTimerIndicator } from "@/components/tasks/RunningTimerIndicator";
import { resolveWorkspaces } from "@/lib/navRegistry";

function ThemeToggle() {
  const { mode, setMode } = useTheme();
  const opts = [{ v: "light", i: Sun }, { v: "dark", i: Moon }, { v: "system", i: Monitor }];
  return (
    <div className="hidden items-center rounded-full border border-border bg-muted/50 p-0.5 md:flex" data-testid="theme-toggle">
      {opts.map((o) => (
        <button key={o.v} onClick={() => setMode(o.v)} data-testid={`theme-${o.v}`}
          className={cn("flex h-7 w-7 items-center justify-center rounded-full transition-colors",
            mode === o.v ? "bg-card text-primary shadow-soft" : "text-muted-foreground hover:text-foreground")}>
          <o.i className="h-3.5 w-3.5" />
        </button>
      ))}
    </div>
  );
}

export function Topbar({ wsId, onMenuClick }) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { config, ctx } = useNavigation();
  const email = user?.email || "";
  const name = ctx?.me?.name || user?.user_metadata?.full_name || email.split("@")[0] || "Member";
  const avatarUrl = ctx?.me?.avatar_url || null;
  const role = ctx?.role || user?.user_metadata?.role || "Member";
  const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const workspaces = resolveWorkspaces(config, ctx);

  const handleSignOut = async () => { await signOut(); navigate("/login"); };

  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border bg-card/80 px-3 backdrop-blur sm:gap-3 sm:px-5">
      <button onClick={onMenuClick} className="lg:hidden" data-testid="menu-button"><Menu className="h-5 w-5 text-foreground" /></button>
      <div className="hidden sm:block"><Logo /></div>
      <div className="sm:hidden"><Logo variant="mobile" size="mobile" /></div>
      <div className="mx-1 hidden h-6 w-px bg-border lg:block" />
      <div className="min-w-0 flex-1 overflow-hidden">
        <WorkspaceNav workspaces={workspaces} currentId={wsId} />
      </div>

      <div className="hidden items-center xl:flex">
        <div className="relative w-56">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search…" className="h-9 pl-9" data-testid="global-search" />
        </div>
      </div>

      <div className="ml-auto flex items-center gap-1.5 sm:gap-2">
        <RunningTimerIndicator />
        <Button variant="ghost" size="icon" className="h-9 w-9 xl:hidden" data-testid="global-search-btn"><Search className="h-[18px] w-[18px]" /></Button>
        <QuickCreate ctx={ctx} />
        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate("/tasks")} data-testid="my-tasks-btn"><ListTodo className="h-[18px] w-[18px]" /></Button>
        <ThemeToggle />
        <Button variant="ghost" size="icon" className="relative h-9 w-9" data-testid="notifications-button">
          <Bell className="h-[18px] w-[18px]" />
          <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-accent" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-full py-1 pl-1 pr-2 transition-colors hover:bg-muted" data-testid="user-menu">
              {avatarUrl
                ? <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" data-testid="topbar-avatar" />
                : <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{initials}</span>}
              <div className="hidden text-left leading-none lg:block">
                <div className="text-sm font-medium text-foreground">{name}</div>
                <div className="text-[11px] text-muted-foreground">{role}</div>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel><div className="text-sm font-medium">{name}</div><div className="text-xs font-normal text-muted-foreground">{email}</div></DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="mr-2 h-4 w-4" /> My Profile</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings"><Settings className="mr-2 h-4 w-4" /> Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} data-testid="menu-signout"><LogOut className="mr-2 h-4 w-4" /> Sign Out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
